export type LogFormat = 'jsonl' | 'structured' | 'env' | 'plain';

export function detectFormat(path: string, content: string): LogFormat {
  if (path.endsWith('.jsonl')) return 'jsonl';
  if (path === '/tmp/devbox-env') return 'env';
  const firstLine = content.split('\n')[0] ?? '';
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(firstLine)) return 'structured';
  return 'plain';
}

export const LEVEL_STYLES: Record<string, string> = {
  error: 'text-status-error',
  warn: 'text-status-warning',
  warning: 'text-status-warning',
  info: 'text-status-info',
  debug: 'text-muted-foreground',
  exec: 'text-status-accent-violet',
};

export const LEVEL_BG: Record<string, string> = {
  error: 'bg-status-error/10 border-l-status-error/50',
  warn: 'bg-status-warning/5 border-l-status-warning/40',
  warning: 'bg-status-warning/5 border-l-status-warning/40',
};

export interface ParsedLogLine {
  raw: string;
  timestamp?: string;
  timeShort?: string;
  context?: string;
  level?: string;
  message?: string;
}

export const STRUCTURED_RE = /^(\d{4}-\d{2}-\d{2}T[\d:.]+Z)\s+\[([^\]]*)\]\s+\[(\w+)\]\s+(.*)/;

export function parseStructuredLine(line: string): ParsedLogLine {
  const m = line.match(STRUCTURED_RE);
  if (!m) return { raw: line };
  const [, timestamp, context, level, message] = m;
  const timeShort = timestamp!.slice(11, 19);
  return {
    raw: line,
    timestamp,
    timeShort,
    context: context ?? '',
    level: level!.toLowerCase(),
    message,
  };
}

export function summarizeJson(obj: Record<string, unknown>): string {
  const payload = obj.payload as string | undefined;
  if (payload && typeof payload === 'string') {
    return payload.length > 80 ? payload.slice(0, 80) + '…' : payload;
  }
  const keys = Object.keys(obj).filter((k) => k !== 'sequence' && k !== 'event_type');
  return keys.map((k) => `${k}: ${truncateValue(obj[k])}`).join(', ');
}

export function truncateValue(v: unknown): string {
  if (typeof v === 'string') return v.length > 40 ? `"${v.slice(0, 40)}…"` : `"${v}"`;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v === null) return 'null';
  if (Array.isArray(v)) return `[${v.length} items]`;
  if (typeof v === 'object') return '{…}';
  return String(v);
}

export const FORMAT_LABELS: Record<LogFormat, string> = {
  jsonl: 'JSON Lines',
  structured: 'Structured',
  env: 'Environment',
  plain: 'Plain text',
};
